import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CheckBox } from '../../../components/inputs/CheckBox';
import { Modal } from '../../../components/Modal';
import { AppliedFilters, AppliedFiltersArray, useAppliedFilters } from '../../../hooks/potree/useAppliedFilters';
import { T, useT, useTranslator } from '../../../translation/src';
import { t } from '../../../potree/translation/markers';
import { FileTypes, PointCloud, GroupType, PrintTemplateSource, useProjectSheetCreateMutation, useProjectSheetMarkExportedMutation, useProjectSheetSaveMutation } from '../../../types/graphqlTypes';
import { Filters } from '../../../components/Filters';
import { Radio, RadioGroup } from '../../../components/inputs/Radio';
import { useProject } from '../../../hooks/potree/useProject';
import { useExportFloors, ObjectExportFormat, canExportType } from '../../../hooks/potree/useExportObjects';
import { useExportPointCloud } from '../../../hooks/potree/useExportPointCloud';
import { useAnnotations } from '../../../hooks/potree/useRenderer';
import { collectLeafAnnotations, isAnnotationContextGroup } from '../../../contexts/AnnotationContext';
import { ArrowRightIcon, CubeIcon, DocumentTextIcon, PencilSquareIcon, RectangleStackIcon, Square3Stack3DIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { Button } from '../../../components/Button';
import { Tooltip } from '../../../components/Tooltip';
import { useCustomSelector } from '../../../redux/store';
import { getFloorPlanePositionProperty } from '@pointorama/potree-shared';
import { floorExportMode as floorExportModeOf, floorWallSource, type FloorExportMode } from '../../../hooks/potree/floorExportMode';
import { GROUP_ICONS_BY_TYPE } from '../../../utils/annotationIcons';
import { RendererContext } from '../../../contexts/RendererContext';
import { buildWallRegionFromRooms } from '../../../potree/utils/WallRegionBuilder';
import { PointoramaRoom } from '../../../potree/utils/PointoramaRoom';
import { hasClosedOutline, type OutlineRingComponent } from '../../../potree/utils/skeleton/outlineRings';
import { writeOutlineRingsToSketch } from '../../../potree/utils/skeleton/buildLayoutWallSketch';
import { PdfExportOptions, capSize, templateKeyOf, type PdfExportSettings, type PdfExportSheetChoice } from '../../print/PdfExportOptions';
import { usePrintTemplates, useQuickPdfExport, type PrintTemplateChoice } from '../../print/export/useQuickPdfExport';
import { useBrandDetailsGate } from '../../templateLibrary/useBrandDetailsGate';
import { QuickPdfPreview, type QuickPreviewSheet, type QuickReframeRequest } from '../../print/QuickPdfPreview';
import { usePlanContent } from '../../print/content/usePlanContent';
import { orgPrintFontsOf } from '../../print/fonts/orgFonts';
import { useSheetPdfExport } from '../../sheets/useSheetPdfExport';
import { useProjectFloors, useSheetSources } from '../../sheets/useSheetContext';
import { useProjectSheets, useSheetTemplate } from '../../sheets/useSheets';
import { PrintRasterAbortError } from '../../../potree/utils/PrintRasterRenderer';
import { parseSheetDoc, type SheetDoc } from 'shared-types';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ReframeOverlay } from '../../sheets/ReframeOverlay';

type AppliedFiltersMap = {
  [key: string]: { filtersArray: AppliedFiltersArray; hasFilters: boolean; filters: AppliedFilters };
};

type FloorItem = {
  identifier: string;
  name: string;
  children: { identifier: string; type: string }[];
  /** Closed outline present → IFC exports the floor as full walls. */
  hasOutline: boolean;
};

/** Wall thickness (m) used to inflate room polygons into an outline; mirrors OutlineTool's default. */
const DEFAULT_WALL_THICKNESS_M = 0.3;

/** Builds an outline from the floor's rooms so IFC can export full walls. */
const CreateOutlineButton: React.FC<{ onCreateOutline: () => void }> = ({ onCreateOutline }) => (
  <Tooltip message={<T _str="Create an outer wall from all rooms on this floor to enable full walls export." />}>
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onCreateOutline();
      }}
      className="flex items-center justify-center gap-1 px-2 py-0.5 text-xs font-medium text-white transition-colors border-2 rounded-lg cursor-pointer bg-french-violet-600 border-french-violet-600 hover:bg-black hover:border-black"
    >
      <Square3Stack3DIcon className="w-3.5 h-3.5" />
      <T _str="Create outer wall" swc />
    </button>
  </Tooltip>
);

/** A format the user picks: name, what it is for (translation marker), icon. */
interface FormatOption<V extends string> {
  value: V;
  label: string;
  hint: string;
  icon: typeof CubeIcon;
}

const extensionOptions: FormatOption<FileTypes>[] = [
  { value: FileTypes.Laz, label: 'LAZ', hint: t('compressed, the smallest file'), icon: CubeIcon },
  { value: FileTypes.Las, label: 'LAS', hint: t('uncompressed, opens everywhere'), icon: CubeIcon },
  { value: FileTypes.Pts, label: 'PTS', hint: t('plain text points'), icon: DocumentTextIcon },
];

/** Floors export as CAD (DXF / IFC) or as a printable PDF sheet per floor. */
type FloorExportFormat = ObjectExportFormat | 'PDF';

const objectFormatOptions: FormatOption<FloorExportFormat>[] = [
  { value: 'PDF', label: 'PDF', hint: t('printable plan at scale'), icon: DocumentTextIcon },
  { value: ObjectExportFormat.DXF, label: 'DXF', hint: t('2D drawing for CAD'), icon: PencilSquareIcon },
  { value: ObjectExportFormat.IFC, label: 'IFC', hint: t('3D model for BIM'), icon: Square3Stack3DIcon },
];

/** One card per format: picking a format reads as a choice between what the files are for. */
function FormatTiles<V extends string>({ options, value, onChange }: { options: FormatOption<V>[]; value: V; onChange: (value: V) => void }) {
  const translate = useTranslator();
  return (
    <div role="radiogroup" className="grid gap-2" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
      {options.map((option) => {
        const selected = option.value === value;
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={classNames(
              'flex flex-col items-start gap-1 rounded-xl border-2 px-3 py-2.5 text-left transition-colors',
              selected ? 'border-french-violet-600 bg-french-violet-600/5 dark:border-neon-green-300 dark:bg-neon-green-300/5' : 'border-gray-200 hover:border-gray-400 dark:border-gray-700 dark:hover:border-gray-500',
            )}
          >
            <span className="flex items-center gap-2 text-sm font-semibold">
              <Icon className={classNames('h-4 w-4', selected ? 'text-french-violet-600 dark:text-neon-green-300' : 'text-gray-400')} />
              {option.label}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{translate(option.hint, { swc: true })}</span>
          </button>
        );
      })}
    </div>
  );
}

/** A titled block of the dialog, with an optional control on the right of the title. */
const Section: React.FC<{ title: React.ReactNode; aside?: React.ReactNode; children: React.ReactNode }> = ({ title, aside, children }) => (
  <section className="flex flex-col gap-2">
    <div className="flex items-center justify-between gap-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</h3>
      {aside}
    </div>
    {children}
  </section>
);

/** PDF prints what the DXF exports, so floors are pickable on the same terms. */
const dataFormatOf = (format: FloorExportFormat): ObjectExportFormat => (format === 'PDF' ? ObjectExportFormat.DXF : format);

// Chosen by the caller (left panel tab), not selectable inside the modal.
export enum ExportType {
  FLOORS = 'FLOORS',
  POINT_CLOUDS = 'POINT_CLOUDS',
}

type PointCloudItem = Pick<PointCloud, 'id' | 'displayName' | 'availableClasses'>;

export interface ExportModalProps {
  onClose: () => void;
  open?: boolean;
  exportType: ExportType;
  /** Opens the project's sheets; omitted when the viewer can't use them. */
  onOpenSheets?: () => void;
}

const floorExportMode = (floor: FloorItem): FloorExportMode => floorExportModeOf(floor.identifier, floor.children);

const isFloorExportable = (floor: FloorItem, format: ObjectExportFormat): boolean => {
  if (floorExportMode(floor) === 'LAYOUT') return true;
  return floor.children.some((c) => canExportType(c.type, format));
};

// Whole floors are selected; no per-object choice.
interface FloorsContentProps {
  floors: FloorItem[];
  selectedFloors: string[];
  setSelectedFloors: React.Dispatch<React.SetStateAction<string[]>>;
  objectFormat: FloorExportFormat;
  onChangeObjectFormat: (format: FloorExportFormat) => void;
  includeLabels: boolean;
  setIncludeLabels: React.Dispatch<React.SetStateAction<boolean>>;
  pdfChoices: PrintTemplateChoice[];
  /** The project's sheets the PDF can follow, and the chosen one's page data (preview). */
  pdfSheets: PdfExportSheetChoice[];
  previewSheet: QuickPreviewSheet | null;
  pdfSettings: PdfExportSettings;
  onChangePdfSettings: (settings: PdfExportSettings) => void;
  /** Opens the project's sheets (null when the viewer can't edit sheets). */
  onOpenSheets: (() => void) | null;
  /** Hands the preview's plan view to the framing overlay (the dialog steps aside meanwhile). */
  onReframe: (request: QuickReframeRequest) => void;
}

const FloorsContent_: React.FC<FloorsContentProps> = ({
  floors,
  selectedFloors,
  setSelectedFloors,
  objectFormat,
  onChangeObjectFormat,
  includeLabels,
  setIncludeLabels,
  pdfChoices,
  pdfSheets,
  previewSheet,
  pdfSettings,
  onChangePdfSettings,
  onOpenSheets,
  onReframe,
}) => {
  const noWallsHint = useT('standardised, but no walls drawn yet: it exports its measured objects until it has walls', { swc: true });
  const { viewer } = useContext(RendererContext);
  const dataFormat = dataFormatOf(objectFormat);

  // Shown but not selectable without content for the format.
  const exportableFloorIds = useMemo(() => floors.filter((f) => isFloorExportable(f, dataFormat)).map((f) => f.identifier), [floors, dataFormat]);

  // Labels only exist in the objects DXF export.
  const showLabelsOption = objectFormat === ObjectExportFormat.DXF && floors.some((f) => selectedFloors.includes(f.identifier) && floorExportMode(f) === 'OBJECTS');

  // Writes an outline inflated from the floor's rooms into its wall sketch
  // (`role:'OUTLINE'` ring edges); the floor's `hasOutline` then flips true.
  const handleCreateOutline = useCallback(
    (floor: FloorItem) => {
      if (!viewer) return;
      const rooms = (viewer.scene.rooms as PointoramaRoom[]).filter((r) => r.groupIdentifier === floor.identifier);
      if (rooms.length === 0) return;

      const roomData = rooms.map((r) => ({ identifier: r.identifier, polygon: r.getPolygon() }));
      const components = buildWallRegionFromRooms(roomData, DEFAULT_WALL_THICKNESS_M);
      if (components.length === 0) return;

      const planePos = getFloorPlanePositionProperty(floor.identifier).value;
      const floorHeight = planePos && typeof planePos === 'object' && 'z' in planePos ? (planePos as { z: number }).z : (roomData[0].polygon[0]?.z ?? 0);

      // One ring component per connected component, holes included.
      const rings: OutlineRingComponent[] = components.map((comp) => ({
        outerRing: comp.outerRing.map((v) => ({ x: v.x, y: v.y, z: floorHeight })),
        holes: comp.holes.map((h) => h.map((v) => ({ x: v.x, y: v.y, z: floorHeight }))),
      }));
      writeOutlineRingsToSketch(viewer, floor.identifier, floorHeight, rings);
    },
    [viewer],
  );

  // The PDF preview: the chosen template, on the first selected floor.
  const previewTemplate = pdfChoices.find((c) => templateKeyOf(c) === pdfSettings.templateKey) ?? null;
  const previewFloorItem = floors.find((f) => selectedFloors.includes(f.identifier) && exportableFloorIds.includes(f.identifier)) ?? null;
  const previewFloor = useMemo(() => (previewFloorItem ? { id: previewFloorItem.identifier, name: previewFloorItem.name } : null), [previewFloorItem]);
  const previewOptions = useMemo(
    () => ({
      scale: pdfSettings.scale,
      framing: pdfSettings.framing,
      textSizes: { labels: capSize(pdfSettings.labelCapMm), dimensions: capSize(pdfSettings.labelCapMm), openings: capSize(pdfSettings.labelCapMm), roomTags: capSize(pdfSettings.roomTagCapMm) },
    }),
    [pdfSettings.scale, pdfSettings.framing, pdfSettings.labelCapMm, pdfSettings.roomTagCapMm],
  );

  if (floors.length === 0) {
    return (
      <div className="p-4 text-sm text-gray-500">
        <T _str="No floors available." />
      </div>
    );
  }

  const GroupIcon = GROUP_ICONS_BY_TYPE[GroupType.Floor];
  const allSelected = exportableFloorIds.length > 0 && exportableFloorIds.every((id) => selectedFloors.includes(id));
  const selectedCount = exportableFloorIds.filter((id) => selectedFloors.includes(id)).length;
  const toggleAll = () =>
    setSelectedFloors((selected) => {
      if (exportableFloorIds.every((id) => selected.includes(id))) return selected.filter((id) => !exportableFloorIds.includes(id));
      return [...new Set([...selected, ...exportableFloorIds])];
    });

  return (
    <div className="flex flex-col gap-5">
      <Section title={<T _str="format" swc />}>
        <FormatTiles options={objectFormatOptions} value={objectFormat} onChange={onChangeObjectFormat} />
      </Section>

      <Section
        title={<T _str="floors" swc />}
        aside={
          <span className="flex items-center gap-3 text-xs">
            <span className="text-gray-500">
              <T _str="{count} of {total} selected" args={{ count: selectedCount, total: exportableFloorIds.length }} />
            </span>
            {exportableFloorIds.length > 1 && (
              <button type="button" className="font-medium text-french-violet-600 hover:underline dark:text-neon-green-300" onClick={toggleAll}>
                {allSelected ? <T _str="clear" swc /> : <T _str="select all" swc />}
              </button>
            )}
          </span>
        }
      >
        <div className="max-h-56 divide-y divide-gray-200 overflow-auto rounded-xl border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
          {floors.map((floor) => {
            const isExportable = exportableFloorIds.includes(floor.identifier);
            const mode = floorExportMode(floor);
            // The tag names the floor's own setting; a standardised floor without walls yet exports its objects (said in the tooltip).
            const standardised = floorWallSource(floor.identifier) === 'WALL_SKETCH';
            const noWallsYet = standardised && mode === 'OBJECTS';
            const showCreateOutline = mode === 'OBJECTS' && objectFormat === ObjectExportFormat.IFC && !floor.hasOutline;
            // Selection survives format switches; never show checked-but-disabled.
            const checked = selectedFloors.includes(floor.identifier) && isExportable;
            return (
              <label
                key={floor.identifier}
                className={classNames('flex items-center gap-3 px-3 py-2.5 text-sm transition-colors', isExportable ? 'cursor-pointer hover:bg-black/[0.03] dark:hover:bg-white/5' : 'cursor-not-allowed opacity-50')}
              >
                <CheckBox
                  checked={checked}
                  disabled={!isExportable}
                  onChange={() => setSelectedFloors((selected) => (selected.includes(floor.identifier) ? selected.filter((id) => id !== floor.identifier) : [...selected, floor.identifier]))}
                />
                <GroupIcon className="h-4 w-4 shrink-0 text-gray-500" />
                <span className="min-w-0 flex-1 truncate">{floor.name}</span>
                {showCreateOutline && <CreateOutlineButton onCreateOutline={() => handleCreateOutline(floor)} />}
                <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600 dark:bg-white/10 dark:text-gray-300" title={noWallsYet ? noWallsHint : undefined}>
                  {standardised ? <T _str="standardised" swc /> : <T _str="as-built" swc />}
                  {noWallsYet && (
                    <span className="text-gray-400">
                      {' '}
                      (<T _str="no walls" />)
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      </Section>

      {showLabelsOption && (
        <Section title={<T _str="options" swc />}>
          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-gray-200 px-3 py-2.5 text-sm dark:border-gray-700">
            <CheckBox checked={includeLabels} onChange={() => setIncludeLabels((v) => !v)} />
            <span className="flex flex-col">
              <T _str="export labels" swc />
              <span className="text-xs text-gray-500">
                <T _str="object names as text in the drawing" swc />
              </span>
            </span>
          </label>
        </Section>
      )}

      {objectFormat === 'PDF' && (
        <Section title={<T _str="print settings" swc />}>
          <QuickPdfPreview template={previewTemplate} sheet={previewSheet} floor={previewFloor} options={previewOptions} onReframe={onReframe} onResetFraming={() => onChangePdfSettings({ ...pdfSettings, framing: null })} />
          <div className="rounded-xl border border-gray-200 px-4 pb-4 dark:border-gray-700">
            <PdfExportOptions choices={pdfChoices} sheets={pdfSheets} value={pdfSettings} onChange={onChangePdfSettings} />
          </div>
          {onOpenSheets && <SheetsCard onOpenSheets={onOpenSheets} />}
        </Section>
      )}
    </div>
  );
};

/** Sheets live behind the export: the place for print layouts beyond a quick PDF. */
const SheetsCard: React.FC<{ onOpenSheets: () => void }> = ({ onOpenSheets }) => (
  <button
    type="button"
    onClick={onOpenSheets}
    className="group flex items-center gap-3 rounded-xl bg-french-violet-600/5 px-4 py-3 text-left transition-colors hover:bg-french-violet-600/10 dark:bg-neon-green-300/5 dark:hover:bg-neon-green-300/10"
  >
    <RectangleStackIcon className="h-6 w-6 shrink-0 text-french-violet-600 dark:text-neon-green-300" />
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="text-sm font-medium">
        <T _str="need more control? use sheets" swc />
      </span>
      <span className="text-xs text-gray-500 dark:text-gray-400">
        <T _str="saved print layouts with title block fields, revisions, several views per page, density maps and heatmaps" swc />
      </span>
    </span>
    <ArrowRightIcon className="h-4 w-4 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5" />
  </button>
);

const FloorsContent = memo(FloorsContent_);

interface PointCloudsTabContentProps {
  pointClouds: PointCloudItem[];
  selectedPointCloud: string | null;
  setSelectedPointCloud: React.Dispatch<React.SetStateAction<string | null>>;
  selectedFilters: { [key: string]: boolean };
  setSelectedFilters: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  appliedFilters: AppliedFiltersMap;
  extension: FileTypes;
  onChangeExtension: (value: FileTypes) => void;
}

const PointCloudsTabContent_: React.FC<PointCloudsTabContentProps> = ({ pointClouds, selectedPointCloud, setSelectedPointCloud, selectedFilters, setSelectedFilters, appliedFilters, extension, onChangeExtension }) => {
  if (pointClouds.length === 0) {
    return (
      <div className="p-4 text-sm text-gray-500">
        <T _str="No point clouds available in this project." />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <Section title={<T _str="point cloud" swc />}>
        <div className="max-h-56 overflow-auto rounded-xl border border-gray-200 p-3 dark:border-gray-700">
          <RadioGroup name="pointcloud_export_selection" value={selectedPointCloud || ''} onChange={({ target: { value } }) => value && setSelectedPointCloud(value)}>
            {pointClouds.map((pointCloud) => {
              const filters = appliedFilters[pointCloud.id]?.filtersArray.filter((filter) => filter.value !== undefined) || [];
              return (
                <Radio key={pointCloud.id} value={pointCloud.id} extraContent={<Filters filters={filters} pointCloudId={pointCloud.id} selectedFilters={selectedFilters} setSelectedFilters={setSelectedFilters} />}>
                  {pointCloud.displayName}
                </Radio>
              );
            })}
          </RadioGroup>
        </div>
      </Section>
      <Section title={<T _str="format" swc />}>
        <FormatTiles options={extensionOptions} value={extension} onChange={onChangeExtension} />
      </Section>
    </div>
  );
};

const PointCloudsTabContent = memo(PointCloudsTabContent_);

// Modal.Container unmounts its children when closed, so the state below
// starts fresh on every open via the useState initializers.
/** The dialog's floor choices, kept while it closes for reframing (the content remounts from them). */
interface ExportMemory {
  selectedFloors?: string[];
  objectFormat?: FloorExportFormat;
  includeLabels?: boolean;
  pdfSettings?: PdfExportSettings;
}

const ExportModalContent_: React.FC<Pick<ExportModalProps, 'onClose' | 'exportType' | 'onOpenSheets'> & { onReframe: (request: QuickReframeRequest) => void; remembered: ExportMemory; onRemember: (memory: ExportMemory) => void }> = ({
  onClose,
  exportType,
  onOpenSheets,
  onReframe,
  remembered,
  onRemember,
}) => {
  const { selectedAnnotations: projectSelectedAnnotations } = useCustomSelector((state) => state.rendererProvider, ['selectedAnnotations']);

  const [{ indexedAnnotations }] = useAnnotations();

  // Only floors export; objects outside a floor group are excluded.
  const floorItems: FloorItem[] = useMemo(
    () =>
      indexedAnnotations.flatMap((a) => {
        if (!isAnnotationContextGroup(a) || a.type !== GroupType.Floor) return [];
        // Leaves only: includes sketches in nested room folders, excludes the
        // folder nodes (GroupType.Room shares the ROOM type string).
        const leaves = collectLeafAnnotations(a);
        return [
          {
            identifier: a.identifier,
            name: a.name,
            hasOutline: hasClosedOutline(
              leaves.map((child) => ({ identifier: child.identifier, type: child.type, groupIdentifier: a.identifier })),
              a.identifier,
            ),
            children: leaves.map((child) => ({ identifier: child.identifier, type: child.type })),
          },
        ];
      }),
    [indexedAnnotations],
  );

  const projectQuery = useProject();
  const pointClouds: PointCloudItem[] = useMemo(
    () =>
      projectQuery.data?.projectById?.pointClouds?.map((pc) => ({
        id: pc.id,
        displayName: pc.displayName,
        availableClasses: pc.availableClasses,
      })) || [],
    [projectQuery.data?.projectById?.pointClouds],
  );

  // Returns an array while the viewer isn't ready.
  const rawAppliedFilters = useAppliedFilters({ project: { pointClouds } });
  const appliedFilters: AppliedFiltersMap = useMemo(() => (Array.isArray(rawAppliedFilters) ? {} : rawAppliedFilters), [rawAppliedFilters]);

  const { exportFloors } = useExportFloors();
  const { exportPointCloud, loading } = useExportPointCloud();

  // Selection is format-agnostic so switching DXF ↔ IFC keeps it.
  const [selectedFloors, setSelectedFloors] = useState<string[]>(() => {
    if (remembered.selectedFloors) return remembered.selectedFloors;
    // Floors selected in the project tree, else all floors.
    const selectedIdentifiers = new Set(projectSelectedAnnotations.map((a) => a.identifier));
    const preSelected = floorItems.filter((f) => selectedIdentifiers.has(f.identifier)).map((f) => f.identifier);
    return preSelected.length > 0 ? preSelected : floorItems.map((f) => f.identifier);
  });
  const [selectedPointCloud, setSelectedPointCloud] = useState<string | null>(() => (pointClouds.length > 0 ? pointClouds[0].id : null));
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: boolean }>(() => Object.entries(appliedFilters).reduce((acc, [key, value]) => ({ ...acc, [key]: value?.hasFilters ?? false }), {}));
  const [extension, setExtension] = useState<FileTypes>(FileTypes.Laz);
  const [objectFormat, setObjectFormat] = useState<FloorExportFormat>(() => remembered.objectFormat ?? 'PDF');
  const [includeLabels, setIncludeLabels] = useState(() => remembered.includeLabels ?? false);
  const { library, choices: pdfChoices, defaultChoice: defaultPdfTemplate } = usePrintTemplates();
  // Sheets the PDF can follow (same template, framing, layers, pens, texts as exporting the sheet itself).
  const { projectId = '' } = useParams();
  const { sheets } = useProjectSheets(projectId);
  const sheetChoices = useMemo(() => [...sheets].sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt))).map((s) => ({ id: s.id, name: s.name, number: s.number })), [sheets]);
  const projectFloors = useProjectFloors();
  const planEntities = usePlanContent();
  const sheetExport = useSheetPdfExport();
  const { ensure: ensureBrandDetails, dialog: brandDetailsDialog } = useBrandDetailsGate(library);
  const [markExported] = useProjectSheetMarkExportedMutation();
  const [createSheet] = useProjectSheetCreateMutation({ refetchQueries: ['projectSheets'] });
  const [saveSheet] = useProjectSheetSaveMutation();
  const keptAsSheet = useT('saved as a sheet: adjust it under Sheets next time', { swc: true });
  /** Creates a sheet from `template` holding what was just printed (floors, framing, scale, text sizes). Best effort: the PDF is out already. */
  const keepAsSheet = useCallback(
    async (template: PrintTemplateChoice, printed: SheetDoc) => {
      try {
        const created = (
          await createSheet({ variables: { projectId, name: template.name, templateSource: template.source === 'org' ? PrintTemplateSource.Org : PrintTemplateSource.Builtin, templateId: template.id } })
        ).data?.projectSheetCreate;
        const parsed = parseSheetDoc(created?.doc);
        if (!created || !('doc' in parsed)) return;
        // The server pinned the template version: keep its reference, take the rest of what printed.
        const doc: SheetDoc = { ...parsed.doc, floorIds: printed.floorIds, viewports: printed.viewports };
        await saveSheet({ variables: { projectId, sheetId: created.id, doc, expectedUpdatedAt: created.updatedAt } });
        await markExported({ variables: { projectId, sheetId: created.id } });
        toast.success(keptAsSheet);
      } catch (e) {
        console.error('The export could not be kept as a sheet', e);
      }
    },
    [createSheet, saveSheet, markExported, projectId, keptAsSheet],
  );
  const [sheetBusy, setSheetBusy] = useState(false);
  const exportFailed = useT('the PDF could not be made', { swc: true });
  const { exportPdf, busy: pdfBusy } = useQuickPdfExport();
  const [pdfSettings, setPdfSettings] = useState<PdfExportSettings>(() => remembered.pdfSettings ?? { templateKey: '', scale: 'fit', labelCapMm: 2.5, roomTagCapMm: 3.5, framing: null });
  // Remember the choices for when the dialog steps aside to reframe.
  useEffect(() => {
    onRemember({ selectedFloors, objectFormat, includeLabels, pdfSettings });
  }, [onRemember, selectedFloors, objectFormat, includeLabels, pdfSettings]);
  // The library loads after the dialog opens: preselect the organisation's default template then.
  const effectivePdfSettings = useMemo(() => {
    let settings = pdfSettings.templateKey || !defaultPdfTemplate ? pdfSettings : { ...pdfSettings, templateKey: templateKeyOf(defaultPdfTemplate) };
    // Not chosen yet: follow the most recently edited sheet, when the project has one.
    if (settings.sheetId === undefined) settings = { ...settings, sheetId: sheetChoices[0]?.id ?? null };
    else if (settings.sheetId && !sheetChoices.some((s) => s.id === settings.sheetId)) settings = { ...settings, sheetId: null };
    return settings;
  }, [pdfSettings, defaultPdfTemplate, sheetChoices]);
  const chosenSheet = effectivePdfSettings.sheetId ? (sheets.find((s) => s.id === effectivePdfSettings.sheetId) ?? null) : null;
  const { template: sheetTemplate } = useSheetTemplate(projectId, chosenSheet, library);
  const sheetSources = useSheetSources(library, chosenSheet ? { name: chosenSheet.name, number: chosenSheet.number } : null);
  const previewSheet = useMemo((): QuickPreviewSheet | null => {
    if (!chosenSheet || !sheetTemplate) return null;
    const parsed = parseSheetDoc(chosenSheet.doc);
    return 'doc' in parsed ? { name: chosenSheet.name, number: chosenSheet.number, spec: sheetTemplate.spec, assets: sheetTemplate.assets, doc: parsed.doc } : null;
  }, [chosenSheet, sheetTemplate]);

  const onChangeExtension = useCallback((value: FileTypes) => {
    setExtension(value);
  }, []);

  const onChangeObjectFormat = useCallback((value: FloorExportFormat) => {
    setObjectFormat(value);
  }, []);

  const onSubmit = useCallback(async () => {
    if (exportType === ExportType.FLOORS && objectFormat === 'PDF' && chosenSheet && previewSheet) {
      // Through the sheet: the same export as from Sheets, for the floors ticked here.
      const floorIds = new Set(floorItems.filter((f) => selectedFloors.includes(f.identifier) && isFloorExportable(f, ObjectExportFormat.DXF)).map((f) => f.identifier));
      const floors = projectFloors.filter((f) => floorIds.has(f.id));
      if (floors.length === 0) return;
      // The brand details this template prints must be filled in (only the missing ones are asked).
      const brandDetails = await ensureBrandDetails(previewSheet.spec);
      if (!brandDetails) return;
      setSheetBusy(true);
      try {
        const [{ createPrintFontRegistry }, { loadPrintImages }] = await Promise.all([import('../../print/fonts/printFonts'), import('../../print/export/printImages')]);
        const fonts = await createPrintFontRegistry(orgPrintFontsOf(library?.config.fonts));
        const images = await loadPrintImages({ logoUrl: brandDetails.logoUrl ?? library?.config.logo?.url ?? null, assets: previewSheet.assets });
        try {
          const projectName = sheetSources.project?.name ?? '';
          await sheetExport.run(
            {
              spec: previewSheet.spec,
              doc: { ...previewSheet.doc, floorIds: floors.map((f) => f.id) },
              floors,
              sources: sheetSources.org ? { ...sheetSources, org: { ...sheetSources.org, ...brandDetails.brand } } : sheetSources,
              fonts,
              images,
              planEntities,
              title: `${projectName} — ${chosenSheet.name}`,
            },
            `${projectName || 'project'} - ${chosenSheet.number ? `${chosenSheet.number} ` : ''}${chosenSheet.name}`,
          );
        } finally {
          images.dispose();
        }
        void markExported({ variables: { projectId, sheetId: chosenSheet.id } });
        onClose();
      } catch (e) {
        if (!(e instanceof PrintRasterAbortError)) {
          console.error('Sheet export failed', e);
          toast.error(exportFailed);
        }
      } finally {
        setSheetBusy(false);
      }
      return;
    }
    if (exportType === ExportType.FLOORS && objectFormat === 'PDF') {
      const template = pdfChoices.find((c) => templateKeyOf(c) === effectivePdfSettings.templateKey);
      const floors = floorItems.filter((f) => selectedFloors.includes(f.identifier) && isFloorExportable(f, ObjectExportFormat.DXF)).map((f) => ({ id: f.identifier, name: f.name }));
      if (!template || floors.length === 0) return;
      const brandDetails = await ensureBrandDetails(template.spec);
      if (!brandDetails) return;
      const printed = await exportPdf({
        template,
        brandDetails,
        floors,
        scale: effectivePdfSettings.scale,
        framing: effectivePdfSettings.framing,
        textSizes: { labels: capSize(effectivePdfSettings.labelCapMm), dimensions: capSize(effectivePdfSettings.labelCapMm), openings: capSize(effectivePdfSettings.labelCapMm), roomTags: capSize(effectivePdfSettings.roomTagCapMm) },
      });
      // Keep the dialog open on failure so the settings aren't lost.
      if (!printed) return;
      // The project's first PDF becomes its first sheet: next time it opens as it printed, to adjust.
      if (sheets.length === 0) await keepAsSheet(template, printed);
      onClose();
      return;
    }
    if (exportType === ExportType.FLOORS && objectFormat !== 'PDF') {
      // The selection may hold floors not exportable in this format.
      const exported = floorItems.filter((f) => selectedFloors.includes(f.identifier) && isFloorExportable(f, objectFormat));
      const asBuiltIds = new Set(exported.filter((f) => floorExportMode(f) === 'OBJECTS').map((f) => f.identifier));
      // Every floor exports its layout — the standardised floors theirs, the as-built floors the band between
      // their outline and rooms with the openings cut out, plus the door/window glyphs and the sketched
      // objects — on top of the objects an as-built floor exports. A floor with nothing of it adds nothing.
      const layoutFloorIds = exported.map((f) => f.identifier);

      // IFC: a floor with a closed outline exports as full walls (built by the
      // export hook) instead of per object.
      const fullWallsFloors = new Set<string>();
      const objects = indexedAnnotations.flatMap((a) => {
        if (!isAnnotationContextGroup(a) || a.type !== GroupType.Floor || !asBuiltIds.has(a.identifier)) return [];
        const leaves = collectLeafAnnotations(a);
        const isFullWalls =
          objectFormat === ObjectExportFormat.IFC &&
          hasClosedOutline(
            leaves.map((c) => ({ identifier: c.identifier, type: c.type, groupIdentifier: a.identifier })),
            a.identifier,
          );
        if (isFullWalls) {
          fullWallsFloors.add(a.identifier);
          return [];
        }
        return leaves.filter((c) => canExportType(c.type, objectFormat));
      });
      // Both paths merge into one file.
      exportFloors({ objects, fullWallsFloors, layoutFloorIds, includeLabels: objectFormat === ObjectExportFormat.DXF && includeLabels }, objectFormat);
    } else if (exportType === ExportType.POINT_CLOUDS) {
      if (!selectedPointCloud) {
        console.error('No point cloud selected');
        return;
      }
      const filters = selectedFilters[selectedPointCloud] && appliedFilters[selectedPointCloud] ? appliedFilters[selectedPointCloud].filters : undefined;
      try {
        await exportPointCloud({
          pointCloudId: selectedPointCloud,
          fileType: extension,
          filters,
        });
      } catch (error) {
        console.error('Export point cloud failed:', error);
      }
    }
    onClose();
  }, [
    keepAsSheet,
    sheets.length,
    ensureBrandDetails,
    exportType,
    indexedAnnotations,
    floorItems,
    selectedFloors,
    objectFormat,
    includeLabels,
    exportFloors,
    selectedPointCloud,
    selectedFilters,
    appliedFilters,
    extension,
    exportPointCloud,
    onClose,
    pdfChoices,
    effectivePdfSettings,
    exportPdf,
    chosenSheet,
    previewSheet,
    projectFloors,
    library,
    sheetSources,
    sheetExport,
    planEntities,
    markExported,
    projectId,
    exportFailed,
  ]);

  // Sheets replace this dialog: close it, then open them.
  const openSheets = useMemo(
    () =>
      onOpenSheets
        ? () => {
            onClose();
            onOpenSheets();
          }
        : null,
    [onClose, onOpenSheets],
  );

  const isSubmitDisabled = useMemo(() => {
    if (exportType === ExportType.FLOORS) {
      if (objectFormat === 'PDF' && (chosenSheet ? !previewSheet : !pdfChoices.some((c) => templateKeyOf(c) === effectivePdfSettings.templateKey))) return true;
      return !floorItems.some((f) => selectedFloors.includes(f.identifier) && isFloorExportable(f, dataFormatOf(objectFormat)));
    }
    return !selectedPointCloud;
  }, [exportType, floorItems, selectedFloors, objectFormat, selectedPointCloud, pdfChoices, effectivePdfSettings.templateKey, chosenSheet, previewSheet]);

  return (
    <>
      {sheetExport.dialog}
      {brandDetailsDialog}
      <Modal.Body className="-mx-6 min-h-0 overflow-y-auto px-6">
        <div>
          {exportType === ExportType.FLOORS && (
            <FloorsContent
              floors={floorItems}
              selectedFloors={selectedFloors}
              setSelectedFloors={setSelectedFloors}
              objectFormat={objectFormat}
              onChangeObjectFormat={onChangeObjectFormat}
              includeLabels={includeLabels}
              setIncludeLabels={setIncludeLabels}
              pdfChoices={pdfChoices}
              pdfSheets={sheetChoices}
              previewSheet={previewSheet}
              pdfSettings={effectivePdfSettings}
              onChangePdfSettings={setPdfSettings}
              onOpenSheets={openSheets}
              onReframe={onReframe}
            />
          )}
          {exportType === ExportType.POINT_CLOUDS && (
            <PointCloudsTabContent
              pointClouds={pointClouds}
              selectedPointCloud={selectedPointCloud}
              setSelectedPointCloud={setSelectedPointCloud}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              appliedFilters={appliedFilters}
              extension={extension}
              onChangeExtension={onChangeExtension}
            />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
        {openSheets && exportType === ExportType.FLOORS && (
          <Button variant="secondary" size="xs" onClick={openSheets}>
            <RectangleStackIcon className="mr-1.5 w-4" />
            <T _str="sheets" swc />
          </Button>
        )}
        <span className="flex-1" />
        <Button variant="secondary" onClick={onClose}>
          <T _str="cancel" swc />
        </Button>
        <Button type="submit" variant="primary" onClick={onSubmit} loading={loading || pdfBusy || sheetBusy} disabled={isSubmitDisabled}>
          {exportType === ExportType.FLOORS ? <T _str="export {format}" args={{ format: objectFormat }} swc /> : <T _str="export {format}" args={{ format: extension.toUpperCase() }} swc />}
        </Button>
      </Modal.Footer>
    </>
  );
};

const ExportModalContent = memo(ExportModalContent_);

const ExportModal_: React.FC2<ExportModalProps> = ({ onClose, open, exportType, onOpenSheets }) => {
  /** Reframing the PDF preview: the dialog closes while the viewer frames the plan, then reopens from what it remembered. */
  const [reframe, setReframe] = useState<QuickReframeRequest | null>(null);
  const [remembered, setRemembered] = useState<ExportMemory>({});
  return (
    <>
      <Modal.Container onClose={onClose} open={open && !reframe} panelClassName="flex max-h-full w-full max-w-2xl flex-col p-6">
        <Modal.Header onClose={onClose} className="!mb-5">
          <span className="flex flex-col">
            <span className="font-semibold">{exportType === ExportType.FLOORS ? <T _str="export floors" swc /> : <T _str="export point clouds" swc />}</span>
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
              {exportType === ExportType.FLOORS ? <T _str="drawings and models of the floors you pick" swc /> : <T _str="the points of one scan, with its filters if you want" swc />}
            </span>
          </span>
        </Modal.Header>
        <ExportModalContent onClose={onClose} exportType={exportType} onOpenSheets={onOpenSheets} onReframe={setReframe} remembered={remembered} onRemember={setRemembered} />
      </Modal.Container>
      {reframe && (
        <ReframeOverlay
          element={reframe.element}
          settings={reframe.settings}
          floorId={reframe.floorId}
          fitPoints={reframe.fitPoints}
          pageLines={reframe.pageLines}
          onConfirm={(frame) => {
            setRemembered((m) => (m.pdfSettings ? { ...m, pdfSettings: { ...m.pdfSettings, framing: { center: frame.center, headingDeg: frame.headingDeg, scaleDen: frame.scaleDen }, scale: frame.scaleDen } } : m));
            setReframe(null);
          }}
          onCancel={() => setReframe(null)}
        />
      )}
    </>
  );
};

export const ExportModal = memo(ExportModal_);
